import React from 'react';
import { Box, Grid } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const OverviewTab = ({ metrics, timeSeries }) => {
  // Merge marketplace data for the chart
  const marketplaceData = timeSeries.orphansListed.map((item, index) => ({
    date: item.date,
    listed: item.count,
    adopted: timeSeries.adoptions[index]?.count || 0,
  }));

  const stats = [
    {
      label: 'User Signups',
      today: metrics.users.today,
      yesterday: metrics.users.yesterday,
      week: metrics.users.last7Days,
      total: metrics.users.total,
      color: '#3B82F6',
    },
    {
      label: 'Teams Listed',
      today: metrics.orphansListed.today,
      yesterday: metrics.orphansListed.yesterday,
      week: metrics.orphansListed.last7Days,
      total: metrics.orphansListed.total,
      active: metrics.orphansListed.active,
      color: '#8B5CF6',
    },
    {
      label: 'Adoptions',
      today: metrics.adoptions.today,
      yesterday: metrics.adoptions.yesterday,
      week: metrics.adoptions.last7Days,
      total: metrics.adoptions.total,
      conversion: metrics.adoptions.conversionRate,
      color: '#10B981',
    },
    {
      label: 'Applications',
      today: metrics.applications.today,
      yesterday: metrics.applications.yesterday,
      week: metrics.applications.last7Days,
      total: metrics.applications.total,
      pending: metrics.applications.pending,
      color: '#F59E0B',
    },
    {
      label: 'Manager Intel Syncs',
      today: metrics.managerIntel.today,
      yesterday: metrics.managerIntel.yesterday,
      week: metrics.managerIntel.last7Days,
      total: metrics.managerIntel.totalSyncs,
      errors: metrics.managerIntel.errorCount,
      color: '#EF4444',
    },
    {
      label: 'Queue Jobs',
      waiting: metrics.queues.managerIntelSync.waiting,
      active: metrics.queues.managerIntelSync.active,
      failed: metrics.queues.managerIntelSync.failed,
      completed: metrics.queues.managerIntelSync.completed,
      color: '#06B6D4',
    },
  ];

  return (
    <Box>
      {/* Critical Alerts */}
      {(metrics.managerIntel.errorCount > 0 || metrics.queues.managerIntelSync.failed > 0) && (
        <Box
          sx={{
            background: '#7F1D1D',
            border: '2px solid #EF4444',
            borderRadius: '12px',
            padding: '20px 24px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              fontSize: '32px',
            }}
          >
            🚨
          </Box>
          <Box>
            <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>
              CRITICAL ALERTS
            </Box>
            <Box sx={{ fontSize: '14px', color: '#FCA5A5' }}>
              {metrics.managerIntel.errorCount} sync errors · {metrics.queues.managerIntelSync.failed} failed jobs
            </Box>
          </Box>
        </Box>
      )}

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ marginBottom: '24px' }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <StatCard stat={stat} />
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ChartCard title="User Growth (14 Days)" color="#3B82F6">
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={timeSeries.users}>
                <defs>
                  <linearGradient id="userGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="date"
                  stroke="#64748B"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    background: '#1E293B',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#F1F5F9',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#userGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard title="Marketplace Activity (14 Days)" color="#10B981">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={marketplaceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="date"
                  stroke="#64748B"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    background: '#1E293B',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#F1F5F9',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="listed"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  dot={false}
                  name="Listed"
                />
                <Line
                  type="monotone"
                  dataKey="adopted"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={false}
                  name="Adopted"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ stat }) => {
  const change = stat.today !== undefined ? stat.today - stat.yesterday : 0;
  const trend = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';

  return (
    <Box
      sx={{
        background: '#1E293B',
        border: '1px solid #334155',
        borderRadius: '12px',
        padding: '20px',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 16px ${stat.color}33`,
          borderColor: stat.color,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <Box sx={{ fontSize: '13px', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase' }}>
          {stat.label}
        </Box>
        <Box
          sx={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: stat.color,
            boxShadow: `0 0 8px ${stat.color}`,
          }}
        />
      </Box>

      {stat.today !== undefined ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '12px' }}>
            <Box sx={{ fontSize: '42px', fontWeight: 700, color: '#F1F5F9', lineHeight: 1 }}>
              {stat.today}
            </Box>
            <Box sx={{ fontSize: '14px', color: '#64748B' }}>today</Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '8px',
              fontSize: '14px',
            }}
          >
            {trend === 'up' && <TrendingUpIcon sx={{ fontSize: '16px', color: '#10B981' }} />}
            {trend === 'down' && <TrendingDownIcon sx={{ fontSize: '16px', color: '#EF4444' }} />}
            {trend === 'neutral' && <RemoveIcon sx={{ fontSize: '16px', color: '#64748B' }} />}
            <Box
              sx={{
                color: trend === 'up' ? '#10B981' : trend === 'down' ? '#EF4444' : '#64748B',
                fontWeight: 600,
              }}
            >
              {trend === 'neutral' ? '—' : change > 0 ? `+${change}` : change}
            </Box>
            <Box sx={{ color: '#64748B' }}>vs yesterday ({stat.yesterday})</Box>
          </Box>

          <Box sx={{ fontSize: '12px', color: '#64748B' }}>
            {stat.week} this week · {stat.total} total
            {stat.active !== undefined && ` · ${stat.active} active`}
            {stat.conversion !== undefined && ` · ${stat.conversion}% conversion`}
            {stat.errors !== undefined && stat.errors > 0 && (
              <Box component="span" sx={{ color: '#EF4444', fontWeight: 600 }}>
                {' '}· {stat.errors} errors
              </Box>
            )}
            {stat.pending !== undefined && ` · ${stat.pending} pending`}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
            <Box>
              <Box sx={{ fontSize: '12px', color: '#64748B', marginBottom: '4px' }}>Waiting</Box>
              <Box sx={{ fontSize: '28px', fontWeight: 700, color: '#F1F5F9' }}>{stat.waiting}</Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: '12px', color: '#64748B', marginBottom: '4px' }}>Active</Box>
              <Box sx={{ fontSize: '28px', fontWeight: 700, color: '#10B981' }}>{stat.active}</Box>
            </Box>
          </Box>
          <Box sx={{ fontSize: '12px', color: '#64748B' }}>
            {stat.failed} failed · {stat.completed} completed
          </Box>
        </>
      )}
    </Box>
  );
};

// Chart Card Component
const ChartCard = ({ title, color, children }) => {
  return (
    <Box
      sx={{
        background: '#1E293B',
        border: '1px solid #334155',
        borderRadius: '12px',
        padding: '24px',
      }}
    >
      <Box
        sx={{
          fontSize: '16px',
          fontWeight: 700,
          color: color,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            width: '4px',
            height: '20px',
            background: color,
            borderRadius: '2px',
          }}
        />
        {title}
      </Box>
      {children}
    </Box>
  );
};

export default OverviewTab;
